// The planet the world is drawn on: render-only curvature (observer-centric LOD, step 6).
//
// The data is a flat periodic tile (the weather's 512 km world, the island's terrain, the cloud windows): positions in it stay what they are, and nothing that reads the data is told
// otherwise. What curves is the picture. Everything is drawn as if the ground fell away round the eye: a point at horizontal distance d from the eye is drawn d^2 / (R + sqrt(R^2 - d^2))
// lower (d^2 / 2R for small d). So the sea, the land, the pools and everything that stands on them are bent by the same function of where they are seen, the horizon dips by
// the angle the geometry says, and a distant cloud sinks behind it.
//
// Three places make it work, each with one job:
//   * the vertex stage (this file's `install`): three's own materials draw each vertex lower by that drop. It is worked out from the vertex's offset from the eye in view space, so it keeps the
//     camera-relative precision three's modelViewMatrix has (a far-off vertex is not rebuilt from world coordinates). Shadow passes are left flat: the shadow lookups are in the data's flat frame;
//   * the sea, the horizon cutter and the ground apron are radial meshes with rings that grow geometrically (`curvedDisc`), because a flat quad cannot bend;
//   * the cloud march (cloudVolume.ts) and the air along it (air.ts) read heights above the curved ground: a straight ray at flat height y is at y + d^2 / 2R above the ground below it.
// The sky shell (dome, sun, moon, stars) is camera-locked and has shaders of its own: it is not bent. The atmosphere's scattering tables (skyPhysics.ts) keep the Earth's own radius whatever
// is set here, so a smaller planet bends the picture, not the sky's colour.
//
// Radius. `EARTH_RADIUS_M` by default; `setRadius(m)` changes it live (0 or less: flat, as it was). The one value is read here, in the cloud march, in skyLight.rayTau and in the sky's
// horizon dip, so they cannot disagree.

import * as THREE from 'three';

/** The Earth's radius, metres: the default. */
export const EARTH_RADIUS_M = 6.371e6;
/** The range a radius control offers (m). */
export const RADIUS_MIN_M = 1.0e5;
export const RADIUS_MAX_M = 2.0e7;

/** The vertex stage's declarations: `uPlanet.x` is the radius (0: flat). */
const PARS_GLSL = /* glsl */`
uniform vec4 uPlanet;                       // (radius in m, 0: flat; unused; unused; unused)
// how far lower than the eye's tangent plane the ground is at horizontal distance sqrt(d2): the exact sphere, in a form that is good for small d
float planetDrop( float d2 ) {
	float R = uPlanet.x;
	return d2 / ( R + sqrt( max( R * R - d2, 0.0 ) ) );
}
`;

/** The same drop for GLSL outside three's own chunks (a ShaderMaterial that declares `uniform vec4 uPlanet` itself, with the same meaning). */
export const PLANET_DROP_GLSL = PARS_GLSL;

/** What the cloud march and the air need: the curvature term a = 1 / (2R) of the height lift `a * d^2`, 0 when flat. */
export function curvatureOf(radius: number): number {
  return radius > 0 && Number.isFinite(radius) ? 0.5 / radius : 0;
}

/** How far lower than the eye's tangent plane the ground is at horizontal distance `d` (m), for radius `radius` (m): the vertex stage's function. */
export function dropAt(d: number, radius: number): number {
  if (!(radius > 0)) return 0;
  const d2 = Math.min(d * d, radius * radius);
  return d2 / (radius + Math.sqrt(Math.max(radius * radius - d2, 0)));
}

/** How far the horizon is from an eye `h` metres above the ground (m): the tangent distance; Infinity when flat. */
export function horizonDistance(h: number, radius: number): number {
  if (!(radius > 0)) return Infinity;
  const hh = Math.max(0, h);
  return Math.sqrt(hh * (2 * radius + hh));
}

/** How far a point `z` metres above the ground can be seen from an eye `h` metres above it (m): the two tangent distances added. */
export function visibleDistance(h: number, z: number, radius: number): number {
  return horizonDistance(h, radius) + horizonDistance(z, radius);
}

export class Planet {
  /** The uniform every program shares (one typed array: a write reaches them all). */
  readonly u = new Float32Array(4);
  readonly uniforms = { uPlanet: { value: this.u } };
  private r = EARTH_RADIUS_M;
  private ok = false;
  private listeners: Array<() => void> = [];
  /** Bumped when the radius changes (a consumer that caches something derived from it compares). */
  version = 0;

  constructor() { this.u[0] = 0; }

  /** True once the vertex stage is patched: until then (or if it could not be) nothing bends, and nothing else should either. */
  get installed(): boolean { return this.ok; }

  /** The radius in use, metres; 0 when flat or when the vertex stage is not there. */
  get radius(): number { return this.ok ? this.r : 0; }

  /** The radius asked for (even when the stage is not installed). */
  get wanted(): number { return this.r; }

  /** The curvature term 1 / (2R), 0 when flat. */
  get curvature(): number { return curvatureOf(this.radius); }

  /** Set the radius (m). 0 or less: flat. */
  setRadius(m: number): void {
    const r = Number.isFinite(m) ? (m > 0 ? Math.min(Math.max(m, RADIUS_MIN_M), RADIUS_MAX_M) : 0) : EARTH_RADIUS_M;
    if (r === this.r) return;
    this.r = r;
    this.u[0] = this.radius;
    this.version++;
    for (const f of this.listeners) f();
  }

  /** Call `f` when the radius changes. Returns the way to stop. */
  subscribe(f: () => void): () => void {
    this.listeners.push(f);
    return () => { this.listeners = this.listeners.filter(g => g !== f); };
  }

  /**
   * Patch three's vertex chunks (once, before the first material compiles): `logdepthbuf_pars_vertex` takes the declarations (it is in every built-in vertex shader, outside its own
   * #ifdef), `project_vertex` bends the vertex. All or nothing: every chunk has to be the text this expects, or none is touched and the world stays flat.
   * `mvFlat` is left in the vertex shader (the vertex's view-space position before the bend) for the air, whose model is the flat data's.
   */
  install(): boolean {
    if (this.ok) return true;
    const chunks = THREE.ShaderChunk as unknown as Record<string, string>;
    const find = 'mvPosition = modelViewMatrix * mvPosition;';
    if (typeof chunks.project_vertex !== 'string' || typeof chunks.logdepthbuf_pars_vertex !== 'string') return false;
    if (chunks.project_vertex.includes('// PLANET')) { this.ok = true; this.u[0] = this.r; return true; }          // patched already (a second renderer, a reload)
    if (!chunks.project_vertex.includes(find) || !chunks.logdepthbuf_pars_vertex.includes('USE_LOGDEPTHBUF')) return false;
    chunks.logdepthbuf_pars_vertex = `// PLANET\n${PARS_GLSL}\n${chunks.logdepthbuf_pars_vertex}`;
    chunks.project_vertex = chunks.project_vertex.replace(find, () => `mvPosition = modelViewMatrix * mvPosition;
	// PLANET: the vertex a little lower the farther it is from the eye (the offset in world axes, from the view-space position); not in the shadow passes, which are in the flat data frame
	vec3 mvFlat = mvPosition.xyz;
	#ifndef DEPTH_PACKING
	if ( uPlanet.x > 0.0 ) {
		vec3 pq = transpose( mat3( viewMatrix ) ) * mvPosition.xyz;
		mvPosition.xyz -= viewMatrix[ 1 ].xyz * planetDrop( dot( pq.xz, pq.xz ) );
	}
	#endif`);
    const lib = THREE.ShaderLib as unknown as Record<string, { uniforms?: Record<string, { value: unknown }> }>;
    for (const k of Object.keys(lib)) { const u = lib[k]!.uniforms; if (u) u.uPlanet = { value: this.u }; }
    this.ok = true;
    this.u[0] = this.r;
    return true;
  }
}

/** The one planet (the vertex chunks are global, so is it). */
export const planet = new Planet();

// ---- meshes that bend -----------------------------------------------------------------------------------------------------------------------

/**
 * A disc in the XZ plane (y = 0, normals up) of radius `outer`, made of rings whose radii grow by the factor `growth` from `first` outward, so the cells are about as long as they are wide
 * (`segments` round the circle) and a surface that falls away as r^2 / 2R is followed to a chord error of c^2 / 8R: centimetres within ten kilometres at the Earth's radius. The vertex stage bends it.
 * `positions` is x, y, z per vertex; the centre is vertex 0.
 */
export function curvedDisc(outer: number, first = 1.5, growth = 1.08, segments = 96): THREE.BufferGeometry {
  const radii: number[] = [];
  for (let r = first; r < outer; r *= growth) radii.push(r);
  radii.push(outer);
  const nr = radii.length;
  const pos = new Float32Array((1 + nr * segments) * 3);
  const nrm = new Float32Array(pos.length);
  for (let i = 0; i < pos.length / 3; i++) nrm[i * 3 + 1] = 1;
  for (let j = 0; j < nr; j++) {
    for (let s = 0; s < segments; s++) {
      const a = (s / segments) * Math.PI * 2, o = (1 + j * segments + s) * 3;
      pos[o] = Math.cos(a) * radii[j]!; pos[o + 2] = Math.sin(a) * radii[j]!;
    }
  }
  const idx: number[] = [];
  // the centre fan, facing up (counter-clockwise seen from above: +y)
  for (let s = 0; s < segments; s++) idx.push(0, 1 + ((s + 1) % segments), 1 + s);
  for (let j = 0; j + 1 < nr; j++) {
    for (let s = 0; s < segments; s++) {
      const a = 1 + j * segments + s, b = 1 + j * segments + ((s + 1) % segments), c = 1 + (j + 1) * segments + s, d = 1 + (j + 1) * segments + ((s + 1) % segments);
      idx.push(a, b, c, b, d, c);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
  g.setIndex(idx.length < 65535 ? new THREE.Uint16BufferAttribute(idx, 1) : new THREE.Uint32BufferAttribute(idx, 1));
  return g;
}
